import React, { useEffect, useState } from "react";
import { pad2 } from "./dateUtil";
import useEscapeKey from "./useEscapeKey";

// 年月ナビ（案B）。細いバー（◀ 年月▾ ▶ ＋今月）＋、ラベルタップで開く月ピッカー。
//  ・PC : ラベル直下にポップオーバー
//  ・スマホ: 下から出るシート（860px以下でCSSが切替）
// value/onChange は "YYYY-MM" 文字列。2モードで動く:
//  ・bounded（社員）: months=記録のある月の昇順配列を渡す。点付き＋データの無い月は無効、矢印は配列内で移動。
//  ・open（経営者）: months を渡さず minYm/maxYm で範囲指定。範囲内は全月選択可（点なし）、矢印は1ヶ月ずつ移動。
// 経営者の勤怠一覧バーにも同じ部品を載せられるよう、画面非依存の純表示コンポーネントにしている。
const parseYm = (ym) => ({ y: parseInt(ym.slice(0, 4), 10), m: parseInt(ym.slice(5, 7), 10) });
const mkYm = (y, m) => `${y}-${pad2(m)}`;
const labelYm = (ym) => { const { y, m } = parseYm(ym); return `${y}年${m}月`; };
function stepYm(ym, delta) {
  let { y, m } = parseYm(ym);
  m += delta;
  while (m < 1) { m += 12; y -= 1; }
  while (m > 12) { m -= 12; y += 1; }
  return mkYm(y, m);
}

const IconChevronDown = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
);

function MonthPicker({ value, months, minYm, maxYm, currentYm, onChange }) {
  const [open, setOpen] = useState(false);
  // ピッカー内で閲覧中の年（選択中の年で初期化し、◀▶で変更）。
  const [pickerYear, setPickerYear] = useState(() => parseYm(value).y);

  // 開くたびに選択中の年へ合わせる。
  useEffect(() => { if (open) setPickerYear(parseYm(value).y); }, [open, value]);

  // Esc で閉じる。
  useEscapeKey(() => setOpen(false), open);

  const bounded = Array.isArray(months) && months.length > 0;
  const loYm = bounded ? months[0] : minYm;
  const hiYm = bounded ? months[months.length - 1] : maxYm;

  // 前後の月（bounded＝配列内の隣、open＝1ヶ月ずつ範囲内でクランプ）。
  let prevYm = null;
  let nextYm = null;
  if (bounded) {
    const idx = months.indexOf(value);
    if (idx > 0) prevYm = months[idx - 1];
    if (idx >= 0 && idx < months.length - 1) nextYm = months[idx + 1];
  } else {
    const p = stepYm(value, -1);
    const n = stepYm(value, 1);
    if (!loYm || p >= loYm) prevYm = p;
    if (!hiYm || n <= hiYm) nextYm = n;
  }

  const pick = (ym) => { onChange(ym); setOpen(false); };
  // 「今月」＝bounded は当月に記録があればそこへ（無ければ最新月）、open は当月を範囲内にクランプ。
  const goToday = () => {
    if (bounded) {
      onChange(months.includes(currentYm) ? currentYm : months[months.length - 1]);
    } else {
      let c = currentYm;
      if (loYm && c < loYm) c = loYm;
      if (hiYm && c > hiYm) c = hiYm;
      onChange(c);
    }
  };

  const minY = parseYm(loYm).y;
  const maxY = parseYm(hiYm).y;
  const monthEnabled = (m) => {
    const ym = mkYm(pickerYear, m);
    if (bounded) return months.includes(ym);
    return (!loYm || ym >= loYm) && (!hiYm || ym <= hiYm);
  };

  return (
    <div className="mp-mnav">
      <div className="mp-datenav mp-mnav-bar">
        <button className="mp-btn mp-ghost mp-sm" aria-label="前の月" disabled={!prevYm} onClick={() => prevYm && onChange(prevYm)}>◀</button>
        <button className="mp-mnav-label" aria-expanded={open} aria-haspopup="dialog" onClick={() => setOpen((o) => !o)}>
          {labelYm(value)}<IconChevronDown />
        </button>
        <button className="mp-btn mp-ghost mp-sm" aria-label="次の月" disabled={!nextYm} onClick={() => nextYm && onChange(nextYm)}>▶</button>
        <button className="mp-btn mp-ghost mp-sm mp-datenav-today" onClick={goToday}>今月</button>
      </div>

      {open && (
        <>
          <div className="mp-mnav-backdrop" onClick={() => setOpen(false)} />
          <div className="mp-mnav-pop" role="dialog" aria-label="年月を選択">
            <div className="mp-mnav-handle" />
            <div className="mp-mnav-yrow">
              <button className="mp-btn mp-ghost mp-sm" aria-label="前の年" disabled={pickerYear <= minY} onClick={() => setPickerYear((y) => y - 1)}>◀</button>
              <span className="mp-mnav-year">{pickerYear}年</span>
              <button className="mp-btn mp-ghost mp-sm" aria-label="次の年" disabled={pickerYear >= maxY} onClick={() => setPickerYear((y) => y + 1)}>▶</button>
            </div>
            <div className="mp-mnav-grid">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
                const ym = mkYm(pickerYear, m);
                const enabled = monthEnabled(m);
                const on = enabled && ym === value;
                return (
                  <button
                    key={m}
                    className={`mp-mnav-chip${on ? " mp-on" : ""}`}
                    disabled={!enabled}
                    onClick={() => pick(ym)}
                  >
                    {m}
                    {bounded && enabled && <span className="mp-mnav-dot" />}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default MonthPicker;
